import { z } from 'zod';

export const JobStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'cancelled']);
export const TwitterSyncJobTypeSchema = z.enum(['twitterSyncTweets', 'twitterSyncLikes', 'twitterSyncBookmarks']);
export const SyncTriggerJobTypeSchema = z.enum(['tweets', 'likes', 'bookmarks']);

export const SyncJobProgressSchema = z.object({
  count: z.number(),
  total: z.number(),
  pct: z.number(),
});

export const SyncJobMetadataSchema = z
  .object({
    error: z.string().nullable().optional(),
    retryAfter: z.string().nullable().optional(),
  })
  .passthrough();

export const SyncJobResponseSchema = z.object({
  id: z.string(),
  jobType: TwitterSyncJobTypeSchema,
  status: JobStatusSchema,
  progress: SyncJobProgressSchema,
  metadata: SyncJobMetadataSchema,
  lastError: z.string().nullable(),
  startedAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  updatedAt: z.date().nullable(),
  createdAt: z.date().nullable(),
});

export const SyncSummaryProgressSchema = z.object({
  jobId: z.string(),
  pct: z.number(),
  count: z.number(),
  total: z.number(),
  message: z.string().nullable(),
  isStale: z.boolean(),
});

export const SyncSummaryEntrySchema = z.object({
  totalItems: z.number(),
  lastCursor: z.string().nullable(),
  lastSyncAt: z.date().nullable(),
  activeJobId: z.string().nullable(),
  progress: SyncSummaryProgressSchema.nullable(),
  status: JobStatusSchema.or(z.literal('idle')),
  isStale: z.boolean(),
});

export const SyncRateLimitStatusSchema = z.object({
  endpoint: z.string(),
  remaining: z.number().nullable().optional(),
  limit: z.number().nullable().optional(),
  resetTime: z.date().nullable().optional(),
  nextRequestAt: z.date().nullable().optional(),
});

export const SyncStatusResponseSchema = z.object({
  summary: z.object({
    tweets: SyncSummaryEntrySchema,
    likes: SyncSummaryEntrySchema,
    bookmarks: SyncSummaryEntrySchema,
  }),
  jobs: z.array(SyncJobResponseSchema),
  rateLimits: z.array(SyncRateLimitStatusSchema),
});

export const SyncTriggerRequestSchema = z.object({
  types: z.array(SyncTriggerJobTypeSchema).min(1).max(3).optional(),
  maxItems: z.number().int().min(1).max(10_000).optional(),
  sinceId: z.string().nullable().optional(),
});

export const SyncTriggerJobResultSchema = z.object({
  type: SyncTriggerJobTypeSchema,
  queued: z.boolean(),
  eventId: z.string().optional(),
  jobId: z.string().optional(),
});

export const SyncTriggerResponseSchema = z.object({
  success: z.literal(true),
  jobs: z.array(SyncTriggerJobResultSchema),
});

export type JobStatus = z.infer<typeof JobStatusSchema>;
export type TwitterSyncJobType = z.infer<typeof TwitterSyncJobTypeSchema>;
export type SyncTriggerJobType = z.infer<typeof SyncTriggerJobTypeSchema>;
export type SyncJobProgress = z.infer<typeof SyncJobProgressSchema>;
export type SyncJobMetadata = z.infer<typeof SyncJobMetadataSchema>;
export type SyncJobResponse = z.infer<typeof SyncJobResponseSchema>;
export type SyncSummaryProgress = z.infer<typeof SyncSummaryProgressSchema>;
export type SyncSummaryEntry = z.infer<typeof SyncSummaryEntrySchema>;
export type SyncRateLimitStatus = z.infer<typeof SyncRateLimitStatusSchema>;
export type SyncStatusResponse = z.infer<typeof SyncStatusResponseSchema>;
export type SyncTriggerRequest = z.infer<typeof SyncTriggerRequestSchema>;
export type SyncTriggerJobResult = z.infer<typeof SyncTriggerJobResultSchema>;
export type SyncTriggerResponse = z.infer<typeof SyncTriggerResponseSchema>;
